import { useState, useEffect, useRef } from "react";
import {
  Group,
  Button,
  LoadingOverlay,
  Image,
  Stack,
  rem,
  Container,
  Grid,
  Flex,
  Box,
  Checkbox,
  Textarea,
  Text,
  Badge,
} from "@mantine/core";
import { useStore } from "../store";
import {
  IconCoins,
  IconCrop,
  IconDownload,
  IconWand,
  IconX,
} from "@tabler/icons-react";
import ProcessingStatusBadge from "../ProcessingStatusBadge";
import mixpanel from "../tracking";
import { notifications } from "@mantine/notifications";
import {
  pollJob,
  createJob,
  uploadImage,
  createFluxImg2Img,
  getJobResults,
} from "../api";
import { Redirect, useLocation } from "wouter";
import { useMediaQuery } from "@mantine/hooks";
import CropForVideo from "./CropForVideo";
import ImageInfo from "./ImageInfo";
import EditorQuickActions from "./EditorQuickActions";
import ResultFeedback from "../_Common/ResultFeedback";

const isDev = import.meta.env.VITE_DEV === "true";

// Instruction-edit cost (mirror of CreditPrices.ImageEditWithLora).
const EDIT_CREDITS = 50;

let baseOperations = [
  { id: "removebg", label: "Remove Background", credits: 30 },
  { id: "upscale", label: "Upscale", credits: 30 },
];
if (isDev) {
  baseOperations = [
    ...baseOperations,
    { id: "test", label: "Test", credits: 1 },
  ];
  console.debug(
    "Development mode, addded test ot baseOperations",
    baseOperations
  );
}

function ImageEditor() {
  const timerIdRef = useRef(null);
  const [job, setJob] = useState({});
  const [isPolling, setIsPolling] = useState(false);
  const [isCropping, setIsCropping] = useState(false);
  const profile = useStore((state) => state.profile);
  const setProfile = useStore((state) => state.setProfile);
  const imageData = useStore((state) => state.imageData);
  const setImageData = useStore((state) => state.setImageData);
  const plugins = useStore((state) => state.pluginData);
  const [editPrompt, setEditPrompt] = useState("");
  const [, setLocation] = useLocation();
  const [highQuality, setHighQuality] = useState(false);
  const isMobile = useMediaQuery("(max-width: 48em)");

  const onCropSave = async (blob) => {
    try {
      setIsPolling(true);
      const data = await uploadImage(blob);
      setIsCropping(false);
      setImageData(data[0]);
    } catch {
      notifications.show({
        title: "Error",
        message: "Failed to crop the image. Please try again.",
        color: "red",
      });
    } finally {
      setIsPolling(false);
    }
  };

  const applyEdit = async (prompt) => {
    if (!prompt || prompt.trim() === "") {
      notifications.show({
        title: "Error",
        message: "Please enter a valid prompt.",
        color: "red",
      });
      return;
    }

    try {
      const data = await createFluxImg2Img({
        prompt: prompt,
        inputImageIds: [imageData.logicalImageId],
      });
      setJob(data);
      setIsPolling(true);
      setProfile({
        ...profile,
        credits: Math.max(0, profile.credits - EDIT_CREDITS),
      });
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error.message,
        color: "red",
      });
    }
  };

  const applyOperation = (operation) => {
    try {
      mixpanel.track("applyOperation called", {
        operationId: operation.id,
        pluginId: operation.pluginId,
      });
    } catch {}

    createJob({
      operation: operation.id,
      InputImageId: imageData.logicalImageId,
      pluginId: operation.pluginId,
      highQuality: highQuality,
    })
      .then((data) => {
        setJob(data);
        setIsPolling(true);
        setProfile({
          ...profile,
          credits: Math.max(0, profile.credits - operation.credits),
        });
      })
      .catch((error) => {
        notifications.show({
          title: "Error",
          message: error.message,
        });
      });
  };

  useEffect(() => {
    const pollingCallback = () => {
      if (!job || !job.jobId) return;

      const status = (job.status ?? "").toLowerCase();
      const pending = status === "processing" || status === "queued";

      if (status === "failed") {
        setIsPolling(false);
        notifications.show({
          title: "Something went wrong!",
          message:
            "Sorry, it seems something went wrong with this job. Please try again later.",
          color: "red",
        });
        return;
      }

      if (status === "success") {
        setIsPolling(false);
        // JobReadDto carries no outputs — fetch the finished job's images and
        // show the first result in place (edits replace the current image).
        getJobResults(job.jobId)
          .then((data) => {
            const img = data?.results?.[0];
            if (img) setImageData(img);
          })
          .catch((err) => {
            console.error("Failed to load edit result", err);
          });
        return;
      }

      if (!pending) {
        setIsPolling(false);
        return;
      }

      pollJob(job.jobId)
        .then((data) => {
          setJob(data);
        })
        .catch((err) => {
          console.error(err);
          setJob(null);
        });
    };

    const startPolling = () => {
      timerIdRef.current = setInterval(pollingCallback, 1500);
    };

    const stopPolling = () => {
      clearInterval(timerIdRef.current);
    };

    if (isPolling) {
      startPolling();
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [isPolling, job, setImageData]);

  let operations = [...baseOperations];
  if (plugins) {
    // PluginActivationReadDto doesn't expose `actions` — v2 manifest plugins
    // never had a notion of editor actions, and v1 plugins never wired them
    // up to the API surface. Without the `?? []` guard, flatMap would emit
    // one `undefined` per plugin and crash on `operation.credits` below.
    operations = operations.concat(plugins.flatMap((p) => p.actions ?? []));
  }

  if (!imageData) return <Redirect to="/generate" />;

  const pixelImage =
    imageData.pixelImages.find((p) => p.type === "Original") ??
    imageData.pixelImages[0];

  const imageUri = pixelImage?.uri;

  if (isCropping) {
    // Crop takes over the editor — single full-width column with its own
    // Cancel/Save controls. Avoids the previous two-column layout where the
    // cropper and its action buttons fought for space on mobile.
    return (
      <Container fluid m={0} p="sm">
        <CropForVideo
          imageUri={imageUri}
          busy={isPolling}
          onCancel={() => setIsCropping(false)}
          onSave={onCropSave}
        />
      </Container>
    );
  }

  return (
    <Container fluid m={0} p={0}>
      <Grid>
        <Grid.Col
          style={{ display: "flex", flexDirection: "column" }}
          span={{ base: 12, sm: 4 }}
          order={{ base: 2, sm: 1 }}
        >
          <Group gap="xs" wrap>
            <Checkbox
              value={highQuality}
              onChange={(event) =>
                setHighQuality(event.currentTarget.checked)
              }
              label="Extra Quality"
              size="xs"
            />

            {operations.map((operation) => (
              <Button
                key={operation.id}
                variant="light"
                size="xs"
                onClick={() => applyOperation(operation)}
                leftSection={
                  <Group wrap="nowrap" gap={4}>
                    <IconCoins
                      stroke={1.5}
                      style={{ width: rem(12), height: rem(12) }}
                    />
                    &nbsp;
                    {operation.credits *
                      (highQuality && operation.type === "imageFilter"
                        ? 2
                        : 1)}
                  </Group>
                }
                loading={isPolling}
                loaderProps={{ type: "dots" }}
              >
                {operation.label}
              </Button>
            ))}
          </Group>
          <Group mt="sm" gap="xs" wrap>
            <Button
              leftSection={
                <IconCrop style={{ width: rem(20) }} stroke={1.5} />
              }
              variant="default"
              size="xs"
              aria-label="Crop"
              onClick={() => {
                setIsCropping(true);
              }}
              disabled={isPolling}
            >
              Crop for video
            </Button>

            <Button
              component="a"
              variant="default"
              size="xs"
              target="_blank"
              href={job?.uri ?? imageData.uri}
              aria-label="Download"
              leftSection={
                <IconDownload style={{ width: rem(20) }} stroke={1.5} />
              }
            >
              Download
            </Button>
            <Button
              leftSection={<IconX style={{ width: rem(20) }} stroke={1.5} />}
              variant="default"
              size="xs"
              aria-label="Close"
              onClick={() => {
                setImageData(null);
                setLocation("/gallery");
              }}
            >
              Close
            </Button>
          </Group>
          <EditorQuickActions
            imageId={imageData.logicalImageId}
            disabled={isPolling}
          />
          <Stack gap="xs" mt="xl">
            <Group>
              <Badge size="sm" color="grape">
                New!
              </Badge>
              <Text fz="sm" c="dimmed" fw={600}>
                Modify Image with AI instructions{" "}
              </Text>
            </Group>
            <Textarea
              placeholder="Describe what you want to change in the image, e.g. 'Make it brighter', 'Add a sunset', 'Remove the person on the left', etc."
              autosize
              required
              value={editPrompt}
              onChange={(e) => setEditPrompt(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && e.shiftKey === false) {
                  e.preventDefault();
                  applyEdit(editPrompt);
                }
              }}
              minRows={3}
              maxRows={6}
            />
            <Button
              disabled={!editPrompt || isPolling}
              onClick={() => applyEdit(editPrompt)}
              loading={isPolling}
              leftSection={
                <Group wrap="nowrap" gap={4}>
                  <IconCoins
                    stroke={1.5}
                    style={{ width: rem(12), height: rem(12) }}
                  />
                  &nbsp;{EDIT_CREDITS}
                </Group>
              }
              rightSection={
                <IconWand
                  stroke={1.5}
                  style={{ width: rem(12), height: rem(12) }}
                />
              }
              size="xs"
            >
              Apply AI
            </Button>
          </Stack>

          <Box mt="md">
            <ResultFeedback
              logicalImageId={imageData.logicalImageId}
              withLabel
            />
          </Box>

          <Flex flex={1} mb="md" />

          <ImageInfo imageData={imageData} pixelImage={pixelImage} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 8 }} order={{ base: 1, sm: 2 }}>
          <Stack
            style={{
              position: "relative",
              height: isMobile
                ? "auto"
                : "calc(100dvh - var(--app-shell-header-height, 0px) - var(--app-shell-header-offset, 0px) - var(--app-shell-footer-height, 0px))",
            }}
            gap={0}
            p={0}
          >
            <LoadingOverlay
              visible={isPolling}
              zIndex={1000}
              overlayProps={{
                blur: 8,
                backgroundOpacity: 0.7,
                color: "#000",
                gradient:
                  "linear-gradient(145deg, rgba(0, 0, 0, 0.95) 0%, rgba(0, 0, 0, 0) 100%)",
              }}
              loaderProps={{ children: <ProcessingStatusBadge job={job} /> }}
            />

            <Image
              radius="md"
              style={{ height: "100%" }}
              fit="contain"
              src={imageUri}
            />
            <div style={{ flex: 1 }}></div>
          </Stack>
        </Grid.Col>
      </Grid>
    </Container>
  );
}

export default ImageEditor;
